import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import MetricModal from 'components/MetricModal';
import Accordion from 'components/Accordion';
import Button from 'components/Button';
import apiRoutes from 'config/apiRoutes';
import useFetch from 'hooks/useFetch';
import useAdmin from 'hooks/useAdmin';
import SectionHeader from 'components/SectionHeader';
import UniversalEmptyState from 'components/EmptyState/UniversalEmptyState';
import { ROUTES } from 'routing/constants';
import { useAuth0 } from '@auth0/auth0-react';
import { format } from 'date-fns';
import {
    accordionCustomStyles,
    MetricAccordionBody,
    LastUpdate,
} from './PerformanceMetrics.style';

const metricsReducer = (state, payload) => ({ ...state, ...payload });

const PerformanceMetrics = ({ handleMetricLoaded, userId }) => {
    const { getAccessTokenSilently, logout } = useAuth0();
    const request = useFetch(getAccessTokenSilently, logout);
    const isAdmin = useAdmin();
    const [disabledSources, setDisabledSources] = useState([]);
    const [metricSources, setMetricSources] = useState([]);
    const [metricsBySourceId, setMetricsBySourceId] = useReducer(
        metricsReducer,
        {}
    );

    const isUserView = !!(userId && userId.length > 0);

    useEffect(() => {
        let reqUrl;

        if (isUserView) {
            reqUrl = apiRoutes.get.organizationMetricSourcesForSingleUser;
        } else {
            reqUrl = isAdmin
                ? apiRoutes.get.organizationMetricSources
                : apiRoutes.get.providerMetricSources;
        }

        request(
            reqUrl,
            'GET',
            (res) => {
                setMetricSources(res.metric_sources);
            },
            (err) => err,
            {},
            { 'Content-Type': 'application/json' }
        );
    }, []);

    const fetchMetricsBySourceId = (sourceId) => {
        let reqUrl;

        if (isUserView) {
            reqUrl = apiRoutes.get.organizationMetricsBySourceIdForSingleUser(
                userId,
                sourceId
            );
        } else {
            reqUrl = isAdmin
                ? apiRoutes.get.organizationMetricsBySourceId(sourceId)
                : apiRoutes.get.metricsBySourceId(sourceId);
        }

        request(
            reqUrl,
            'GET',
            (res) => {
                setMetricsBySourceId({
                    [sourceId]: [...res.metrics],
                });
            },
            (err) => err,
            {},
            { 'Content-Type': 'application/json' }
        );
    };

    useEffect(() => {
        // Populate the metrics state so accordions can be opened on page render
        metricSources.forEach((ms) => fetchMetricsBySourceId(ms.id));
        if (isAdmin) {
            setDisabledSources(
                metricSources.filter((ms) => !ms.enabled).map((ms) => ms.id)
            );
        }
    }, [metricSources]);

    useEffect(() => {
        handleMetricLoaded();
    }, [metricsBySourceId, metricSources]);

    const forAdmin = isAdmin && !isUserView;

    const renderMetricResult = (metric) =>
        metric.smart_tip_id ? (
            <Link
                to={`${ROUTES.smartTips}/${metric.smart_tip_id}`}
                key={metric.id}
                style={{ maxWidth: 'calc(100vw - 48px)' }}
            >
                <MetricModal
                    metric={metric}
                    forAdmin={forAdmin}
                    customCta={() => (
                        <Button primary fullWidth>
                            Go to Smart Tip
                        </Button>
                    )}
                />
            </Link>
        ) : (
            <MetricModal key={metric.id} metric={metric} forAdmin={forAdmin} />
        );

    if ((!isAdmin && !metricSources) || metricSources.length === 0) {
        return <></>;
    }

    return (
        <>
            <SectionHeader
                title={
                    forAdmin
                        ? 'Overall Performance Metrics'
                        : 'Individual Performance Metrics'
                }
            />
            {isAdmin && (!metricSources || metricSources.length === 0) && (
                <UniversalEmptyState description="There are no Metrics or Data Sources available to your organization at the moment." />
            )}
            {metricSources.map((metricSource) => (
                <Accordion
                    key={metricSource.id}
                    label={metricSource.name}
                    customStyles={accordionCustomStyles}
                    customOnClick={() =>
                        fetchMetricsBySourceId(metricSource.id)
                    }
                    rightSideCta={() => {
                        if (
                            metricSource.measure_started_at &&
                            metricSource.measured_at
                        ) {
                            const from = format(
                                new Date(metricSource.measure_started_at),
                                'MMM yyyy'
                            );
                            const to = format(
                                new Date(metricSource.measured_at),
                                'MMM yyyy'
                            );
                            return from === to ? (
                                <LastUpdate> Data from: {from}</LastUpdate>
                            ) : (
                                <LastUpdate>
                                    Data from: {from} {' - '} {to}
                                </LastUpdate>
                            );
                        }
                        return <></>;
                    }}
                    disabledSource={disabledSources.includes(metricSource.id)}
                    initiallyOpen
                >
                    <MetricAccordionBody>
                        {metricsBySourceId[metricSource.id] &&
                            metricsBySourceId[metricSource.id].map(
                                (singleMetric) =>
                                    renderMetricResult(singleMetric)
                            )}
                    </MetricAccordionBody>
                </Accordion>
            ))}
        </>
    );
};

PerformanceMetrics.propTypes = {
    userId: PropTypes.oneOfType([PropTypes.string, undefined]),
    handleMetricLoaded: PropTypes.func.isRequired,
};

PerformanceMetrics.defaultProps = {
    userId: undefined,
};

export default PerformanceMetrics;
